import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { colorOf, fmtDateTime, schApi, schErr } from "@/lib/schApi";
import { btnGold, btnGreen, btnRed, field, useLoad } from "@/components/nds/windows/finance/parts";
import { ConflictBadge } from "@/components/nds/windows/sch/parts";

function PublicBookings({ id }) {
  const [s] = useLoad(() => schApi.get(`/events/${id}/public-bookings`), [id]);
  const rows = s.data?.bookings || [];
  return (
    <div data-testid="sch-detail-public-bookings" className="space-y-1">
      <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">Public bookings · {rows.filter((b) => b.status === "active").length}</div>
      {rows.map((b) => (
        <div key={b.id} className={`text-xs ${b.status === "active" ? "text-white/85" : "text-nex-muted line-through"}`}>
          {b.name} · {b.email} · {b.phone}{b.duplicate_flag && <span className="ml-1 text-amber-300">(duplicate email)</span>}
        </div>
      ))}
    </div>
  );
}

const Row = ({ k, children }) => <div className="flex gap-2 text-xs"><span className="w-24 shrink-0 text-nex-muted">{k}</span><span className="text-white/85 min-w-0">{children}</span></div>;

/** Side panel: assignees, capacity bar, conflict status, edit / cancel / complete. */
export default function EventDetail({ id, meta, onClose, onEdit, onChanged, embedded = false }) {
  const [state, load] = useLoad(() => schApi.get(`/events/${id}`), [id]);
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState("");
  const [applyTo, setApplyTo] = useState("this");
  if (!state.data) return <aside data-testid="sch-detail" className="w-full md:w-[340px] shrink-0 border-l border-white/10 p-4 text-xs text-nex-muted">{state.error ? schErr(state.error) : "Loading…"}</aside>;
  const e = state.data;
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); setCancelling(false); load(); onChanged(); } catch (err) { toast.error(schErr(err)); } };
  const pct = e.max_capacity ? Math.min(100, (e.current_enrollment / e.max_capacity) * 100) : 0;
  const t = meta.event_types[e.event_type];
  return (
    <aside data-testid="sch-detail" className="w-full md:w-[340px] shrink-0 border-l border-white/10 overflow-y-auto p-4 space-y-3 bg-black/20">
      <div className="flex items-start gap-2">
        <span className="mt-1 h-3 w-3 rounded-full shrink-0" style={{ background: colorOf(e) }} />
        <h3 data-testid="sch-detail-title" className="flex-1 text-base font-semibold text-white">{e.title}</h3>
        {!embedded && <button type="button" onClick={onClose} data-testid="sch-detail-close" className="text-nex-muted hover:text-white"><X className="h-4 w-4" /></button>}
      </div>
      <div className="flex flex-wrap gap-1.5 items-center">
        <span className="font-mono text-[10px] uppercase tracking-wider px-1.5 rounded-sm" style={{ background: `${colorOf(e)}33`, color: colorOf(e) }}>{t?.label}</span>
        <span data-testid="sch-detail-status" className="font-mono text-[10px] uppercase tracking-wider text-nex-muted">{e.status}</span>
        {e.is_mandatory && <span className="font-mono text-[10px] uppercase text-amber-300">Mandatory</span>}
        {e.has_conflicts && <ConflictBadge testid="sch-detail-conflict-badge" />}
      </div>
      <Row k="When">{fmtDateTime(e.start_time)} to {fmtDateTime(e.end_time)}</Row>
      <Row k="Location">{e.location || "—"}</Row>
      <Row k="Visibility">{e.visibility}</Row>
      {e.series_id && <Row k="Series">Occurrence {e.occurrence_index + 1}</Row>}
      <div data-testid="sch-detail-capacity" className="space-y-1">
        <div className="flex justify-between text-xs"><span className="text-nex-muted">Capacity</span><span className="text-white/85">{e.current_enrollment}{e.max_capacity ? ` / ${e.max_capacity}` : " (no limit)"}</span></div>
        {e.max_capacity && <div className="h-1.5 rounded-full bg-white/10 overflow-hidden"><div className={`h-full ${pct >= 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-400" : "bg-emerald-400"}`} style={{ width: `${pct}%` }} /></div>}
      </div>
      <Row k="Residents"><span data-testid="sch-detail-residents">{e.assigned_resident_ids.map((x) => e.names[x] || x).join(", ") || "—"}</span></Row>
      <Row k="Staff"><span data-testid="sch-detail-staff">{e.assigned_staff_ids.map((x) => e.names[x] || x).join(", ") || "—"}</span></Row>
      {e.partner_id && <Row k="Partner">{meta.partners.find((p) => p.id === e.partner_id)?.name || e.partner_id}</Row>}
      {e.notes && <Row k="Notes">{e.notes}</Row>}
      {e.shared_program && <Row k="Shared">All partners can see this</Row>}
      {e.visibility === "public" && <PublicBookings id={e.id} />}
      <div data-testid="sch-detail-conflicts" className="space-y-1">
        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-nex-muted">Conflict status</div>
        {e.conflicts.length ? e.conflicts.map((c, i) => (
          <div key={i} className={`text-xs rounded-sm px-2 py-1 ${c.severity === "hard_block" ? "bg-red-500/10 text-red-200" : "bg-amber-500/10 text-amber-200"}`}>
            {c.message}<div className="text-[10px] opacity-70">{c.resolution} by {c.by?.name} {c.reason ? `· "${c.reason}"` : ""}</div>
          </div>
        )) : <div className="text-xs text-emerald-300">No conflicts</div>}
      </div>
      {e.cancel_reason && <Row k="Cancelled">{e.cancel_reason}</Row>}
      {e.status === "active" && (
        <div className="flex flex-wrap gap-2 pt-2">
          <button type="button" onClick={() => onEdit(e)} data-testid="sch-detail-edit" className={btnGold}>Edit</button>
          <button type="button" onClick={() => act(() => schApi.post(`/events/${e.id}/complete`), "Marked completed")} data-testid="sch-detail-complete" className={btnGreen}>Complete</button>
          {!cancelling && <button type="button" onClick={() => setCancelling(true)} data-testid="sch-detail-cancel" className={btnRed}>Cancel event</button>}
        </div>
      )}
      {cancelling && (
        <div className="space-y-2" data-testid="sch-cancel-box">
          <input data-testid="sch-cancel-reason" className={`${field} w-full`} placeholder="Reason (optional)" value={reason} onChange={(ev) => setReason(ev.target.value)} />
          {e.series_id && <select data-testid="sch-cancel-apply-to" className={`${field} w-full`} value={applyTo} onChange={(ev) => setApplyTo(ev.target.value)}><option value="this">Only this occurrence</option><option value="future">This and all following</option></select>}
          <button type="button" onClick={() => act(() => schApi.post(`/events/${e.id}/cancel`, { reason, apply_to: applyTo }), "Event cancelled")} data-testid="sch-cancel-confirm" className={btnRed}>Confirm cancel</button>
        </div>
      )}
    </aside>
  );
}
